import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AdminShell } from "@/components/AdminShell";
import { AppShell } from "@/components/AppShell";
import { AuthedRedirect } from "@/components/AuthedRedirect";
import { InlineSpinner } from "@/components/InlineSpinner";
import { OnboardingGate } from "@/components/OnboardingGate";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider } from "@/lib/auth";
import { MunicipalityProvider } from "@/lib/municipality";
import { AceptarInvitacion } from "@/pages/AceptarInvitacion";
import { ActualizacionLegal } from "@/pages/ActualizacionLegal";
import { Alertas } from "@/pages/Alertas";
import { Cuenta } from "@/pages/Cuenta";
import { Expedientes } from "@/pages/Expedientes";
import { ExpedienteDetail } from "@/pages/ExpedienteDetail";
import { FichaMunicipal } from "@/pages/FichaMunicipal";
import { HelloKaptar } from "@/pages/HelloKaptar";
import { Landing } from "@/pages/Landing";
import { LegalPublico } from "@/pages/LegalPublico";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { Login } from "@/pages/Login";
import { Membresia } from "@/pages/Membresia";
import { Onboarding } from "@/pages/Onboarding";
import { OportunidadesPerdidas } from "@/pages/OportunidadesPerdidas";
import { Radar } from "@/pages/Radar";
import { RenovarMembresia } from "@/pages/RenovarMembresia";
import { ResetPassword } from "@/pages/ResetPassword";
import { Vinculacion } from "@/pages/Vinculacion";

// Rutas /admin/* (Sprint Blindaje — PERF-1, julio 2026): code splitting.
// Estas 9 páginas solo las visita el staff interno de Kaptar (ver
// ADR-016), nunca el funcionario municipal — cargarlas de forma
// estática inflaba el chunk inicial que SÍ descarga cada sesión. Con
// `React.lazy` cada una vive en su propio chunk que el browser solo
// pide al navegar a /admin/*.
const Admin = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.Admin })));
const AdminAnalytics = lazy(() =>
  import("@/pages/AdminAnalytics").then((m) => ({ default: m.AdminAnalytics })),
);
const AdminAssistant = lazy(() =>
  import("@/pages/AdminAssistant").then((m) => ({ default: m.AdminAssistant })),
);
const AdminAssistantAudit = lazy(() =>
  import("@/pages/AdminAssistantAudit").then((m) => ({
    default: m.AdminAssistantAudit,
  })),
);
const AdminFreshness = lazy(() =>
  import("@/pages/AdminFreshness").then((m) => ({ default: m.AdminFreshness })),
);
const AdminOpportunities = lazy(() =>
  import("@/pages/AdminOpportunities").then((m) => ({
    default: m.AdminOpportunities,
  })),
);
const AdminScrapers = lazy(() =>
  import("@/pages/AdminScrapers").then((m) => ({ default: m.AdminScrapers })),
);
const AdminStaff = lazy(() =>
  import("@/pages/AdminStaff").then((m) => ({ default: m.AdminStaff })),
);
const AdminTenants = lazy(() =>
  import("@/pages/AdminTenants").then((m) => ({ default: m.AdminTenants })),
);

/** Fallback minimo mientras el chunk de una pagina /admin/* descarga. */
function AdminRouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <InlineSpinner tone="brand" className="h-6 w-6 border-2" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <MunicipalityProvider>
            <Routes>
              {/* Pública */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/aceptar-invitacion" element={<AceptarInvitacion />} />
              {/* Vinculación de un municipio nuevo (Sprint 25-4, mockup M3):
                  la abre un funcionario sin sesión desde un correo, así que
                  va sin ProtectedRoute y sin AppShell — mismo patrón que
                  /aceptar-invitacion. */}
              <Route path="/vinculacion/:token" element={<Vinculacion />} />

              {/* Debug local — health check del backend. Se remueve cuando
                  haya un dashboard interno de ops. */}
              <Route path="/debug" element={<HelloKaptar />} />

              {/* Root: si hay sesión → /radar; si no → landing pública. */}
              <Route
                path="/"
                element={
                  <AuthedRedirect whenAuthed="/radar">
                    <Landing />
                  </AuthedRedirect>
                }
              />

              {/* Onboarding: requiere auth pero NO requiere muni activo
                  (ese es justamente el punto del wizard). */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />

              {/* Documentos legales públicos: la versión canónica del
                  texto (la landing redirige sus /legal/* aquí). */}
              <Route path="/legal/:doc" element={<LegalPublico />} />

              {/* Actualización legal: requiere auth pero NO pasa por
                  OnboardingGate, que es quien manda aquí (redirigiría a
                  esta misma ruta). Mismo patrón que /membresia/renovar. */}
              <Route
                path="/actualizacion-legal"
                element={
                  <ProtectedRoute>
                    <ActualizacionLegal />
                  </ProtectedRoute>
                }
              />

              {/* Renovación de membresía: requiere auth pero NO pasa por
                  OnboardingGate — a esta ruta la manda TenantPausado
                  cuando el tenant está pausado por falta de renovación,
                  así que el gate (que redirigiría de vuelta a
                  TenantPausado) tiene que quedar afuera. Mismo patrón
                  que /onboarding. */}
              <Route
                path="/membresia/renovar"
                element={
                  <ProtectedRoute>
                    <RenovarMembresia />
                  </ProtectedRoute>
                }
              />

              {/* Rutas autenticadas con muni activo obligatorio. */}
              <Route
                element={
                  <ProtectedRoute>
                    <OnboardingGate>
                      <AppShell />
                    </OnboardingGate>
                  </ProtectedRoute>
                }
              >
                <Route path="/radar" element={<Radar />} />
                <Route path="/municipio/:dane" element={<FichaMunicipal />} />
                <Route path="/expedientes" element={<Expedientes />} />
                <Route path="/expedientes/:id" element={<ExpedienteDetail />} />
                <Route path="/alertas" element={<Alertas />} />
                <Route path="/oportunidades-perdidas" element={<OportunidadesPerdidas />} />
                <Route path="/membresia" element={<Membresia />} />
                <Route path="/cuenta" element={<Cuenta />} />
              </Route>

              {/* Rutas admin (Sprint 11 · 1, ADR-016): AdminShell separado.
                  No pasa por OnboardingGate porque staff puro (sin tenant)
                  igual debe poder entrar. El AdminShell frena al no-staff
                  al montar con "Acceso restringido" (issue #579); la puerta
                  real sigue siendo el 403 del backend. */}
              <Route
                element={
                  <ProtectedRoute>
                    <AdminShell />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="/admin"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <Admin />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/analytics"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <AdminAnalytics />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/asistente"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <AdminAssistant />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/asistente-auditoria"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <AdminAssistantAudit />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/staff"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <AdminStaff />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/convocatorias"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <AdminOpportunities />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/tenants"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <AdminTenants />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/fuentes"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <AdminFreshness />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/scrapers"
                  element={
                    <Suspense fallback={<AdminRouteFallback />}>
                      <AdminScrapers />
                    </Suspense>
                  }
                />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </MunicipalityProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
